import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import { darken } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import AppRouter, { RouterContext } from 'components/Router';
import { WithStyles } from 'decorators/withStyles';
import { IAppRoute } from 'interfaces/route';
import { IUserToken } from 'interfaces/userToken';
import CircleIcon from 'mdi-react/CircleIcon';
import ExpandLessIcon from 'mdi-react/ExpandLessIcon';
import ExpandMoreIcon from 'mdi-react/ExpandMoreIcon';
import React, { Fragment, memo, PureComponent } from 'react';
import rxjsOperators from 'rxjs-operators';

import { IAppRouteParsed } from './routeParser';

interface IState {
  expanded: boolean;
  active: boolean;
}

interface IProps {
  user: IUserToken;
  route: IAppRouteParsed;
  hasParent?: boolean;
  onClick: (route: IAppRoute) => void;
  classes?: any;
}

@WithStyles((theme) => ({
  item: {
    opacity: 0.8,
    cursor: 'pointer',
    '&.active': {
      opacity: 1,
    },
    '&:hover': {
      opacity: 1,
    },
  },
  icon: {
    fill: theme.palette.primary.main,
  },
  text: {
    color: theme.palette.primary.main,
    margin: 0,
  },
  expandablePanel: {
    background: theme.palette.primary.contrastText,
    color: theme.palette.primary.main,
    boxShadow: 'none',
    margin: 0,
  },
  expandableTitleContent: {
    alignItems: 'center',
  },
  listItemText: {
    margin: 0,
  },
  expandableTitle: {
    paddingLeft: theme.spacing(2),
    '&:hover': {
      background: darken(theme.palette.primary.contrastText, 0.1),
    },
  },
  expandableDetails: {
    padding: 0,
  },
  innerList: {
    paddingLeft: 14,
    padding: 0,
    width: '100%',
  },
  iconButton: {
    backgroundColor: 'unset !important',
  },
  childrenText: {
    color: theme.palette.text.secondary,
  },
  iconParent: {
    fill: theme.palette.text.secondary,
    opacity: 0.5,
  },
}))
class DrawerListItem extends PureComponent<IProps, IState> {
  getRouter: () => AppRouter;

  constructor(props: IProps) {
    super(props);
    this.state = { expanded: false, active: false };
  }

  componentDidMount() {
    this.getRouter()
      .observeChange()
      .pipe(rxjsOperators.logError(), rxjsOperators.bindComponent(this))
      .subscribe((location) => {
        const { route } = this.props;

        let active = route.exact
          ? location.pathname === route.path
          : location.pathname.startsWith(route.path);

        if (active && route.path.includes('entity')) {
          active = location.pathname === route.path;
        }

        this.setState({
          active,
          expanded: active || route.subMenuDefault === 'open',
        });
      });
  }

  handleClick = () => {
    this.props.onClick(this.props.route);
  };

  handleSubClick = (route: IAppRouteParsed) => {
    this.props.onClick(route);
  };

  handleExandedClick = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  canAccess = () => {
    const { route, user } = this.props;

    if (route.allowAnonymous) return true;
    if (!user) return false;
    if (!route.roles) return true;

    return user.canAccess(...route.roles);
  };

  render() {
    const { route } = this.props;

    return (
      <Fragment>
        <RouterContext.Consumer>
          {(getRouter) => (this.getRouter = getRouter) && null}
        </RouterContext.Consumer>

        {this.canAccess() &&
          (!route.subRoutes.length ? this.renderSingle() : this.renderList())}
      </Fragment>
    );
  }

  renderSingle = () => {
    const { active } = this.state;
    const { route, classes, hasParent } = this.props;

    return (
      <ListItem
        className={`${classes.item} ${active ? 'active' : ''}`}
        onClick={this.handleClick}
      >
        {!route.sideDrawer.icon && hasParent && (
          <ListItemIcon
            className={classes.icon}
            classes={{ root: classes.text }}
          >
            {active && <CircleIcon size={12} />}
          </ListItemIcon>
        )}

        {!!route.sideDrawer.icon && (
          <ListItemIcon
            className={classes.icon}
            classes={{ root: classes.text }}
          >
            <route.sideDrawer.icon size={19} />
          </ListItemIcon>
        )}
        <ListItemText
          primary={route.sideDrawer.display}
          classes={{
            primary: clsx(classes.text, {
              [classes.childrenText]: hasParent && !active,
            }),
          }}
          className={classes.listItemText}
        />
      </ListItem>
    );
  };

  renderList = (): React.ReactNode => {
    const { expanded, active } = this.state;
    const { route, classes, user, hasParent } = this.props;

    return (
      <>
        <ListItem className={classes.item} onClick={this.handleExandedClick}>
          {!route.sideDrawer.icon && hasParent && (
            <ListItemIcon
              className={classes.icon}
              classes={{ root: classes.text }}
            >
              {active && <CircleIcon size={12} />}
            </ListItemIcon>
          )}
          {route.sideDrawer.icon && (
            <ListItemIcon
              className={classes.icon}
              classes={{ root: classes.text }}
            >
              <route.sideDrawer.icon size={19} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={route.sideDrawer.display}
            classes={{
              primary: clsx(classes.text, {
                [classes.childrenText]: hasParent && !active,
              }),
            }}
            className={classes.listItemText}
          />
          <ListItemSecondaryAction>
            <IconButton
              size="small"
              onClick={this.handleExandedClick}
              className={classes.iconButton}
              disableTouchRipple
              disableRipple
              disableFocusRipple
            >
              {!expanded ? (
                <ExpandMoreIcon className={classes.iconParent} />
              ) : (
                <ExpandLessIcon className={classes.iconParent} />
              )}
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <List className={classes.innerList}>
              {route.subRoutes.map((sub) => (
                <DrawerListItem
                  key={sub.path}
                  user={user}
                  route={sub}
                  classes={classes}
                  onClick={this.handleSubClick}
                  hasParent
                />
              ))}
            </List>
          </List>
        </Collapse>
      </>
    );
  };
}

export default memo(DrawerListItem);
